// Vendor Program v2 PR 3 — "Reports awaiting review" queue on the VFI
// Accounting page. Every status=submitted monthly vendor report, oldest
// submission first; a row opens PR 2's shared VendorReportReviewDrawer
// (accept / return) via the page-owned ?report=<id> deep link.

import { formatPeriodMonth } from "@/design-system/lib/format";
import { useMemo } from "react";
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useVendorReports } from "@/hooks/portfolioAdmin/useVendorReports";
import { fmtMoney } from "../billing/vendorBillingFormat";

interface SubmittedReportsQueueProps {
  onOpenReport: (reportId: string) => void;
}

export function SubmittedReportsQueue({ onOpenReport }: SubmittedReportsQueueProps) {
  const reportsQuery = useVendorReports({ status: "submitted" });

  const rows = useMemo(
    () =>
      [...(reportsQuery.data ?? [])].sort((a, b) =>
        (a.submittedAt ?? "").localeCompare(b.submittedAt ?? ""),
      ),
    [reportsQuery.data],
  );

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  return (
    <Card data-testid="vfi-reports-queue">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Reports awaiting review
      </h3>

      {reportsQuery.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="vfi-reports-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {reportsQuery.isError && (
        <div data-testid="vfi-reports-error">
          <p className="text-small text-status-error m-0" role="alert">
            Could not load the report review queue.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-space-2"
            onClick={() => void reportsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {reportsQuery.isSuccess && rows.length === 0 && (
        <div data-testid="vfi-reports-empty">
          <EmptyState
            variant="empty"
            title="No reports awaiting review"
            description="Submitted monthly vendor reports land here for accept / return."
          />
        </div>
      )}

      {reportsQuery.isSuccess && rows.length > 0 && (
        <Table data-testid="vfi-reports-table">
          <THead>
            <TR>
              <TH className="text-white-pure">Period</TH>
              <TH className="text-white-pure">Vendor</TH>
              <TH className="text-white-pure">Loan</TH>
              <TH className="text-white-pure">Revision</TH>
              <TH className="text-white-pure text-right">Amount</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR
                key={r.id}
                onClick={() => onOpenReport(r.id)}
                className="cursor-pointer"
                data-testid={`vfi-report-row-${r.id}`}
              >
                <TD className="whitespace-nowrap">{month(r.periodStart)}</TD>
                <TD>{r.vendorName}</TD>
                <TD>{r.loanNumber}</TD>
                <TD>
                  {r.revisionNumber > 1 ? (
                    <StatusBadge
                      tone="warn"
                      hint={`Revision ${r.revisionNumber} — the vendor resubmitted after a return.`}
                    >
                      Resubmitted
                    </StatusBadge>
                  ) : (
                    <span className="text-gray-mid">—</span>
                  )}
                </TD>
                <TD className="text-right tabular-nums">
                  {r.selfReportedInvoiceAmount != null
                    ? fmtMoney(r.selfReportedInvoiceAmount)
                    : "—"}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
